import { Component, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import Drilldown from 'highcharts/modules/drilldown';
Drilldown(Highcharts);
import { HighchartsChartModule } from 'highcharts-angular';
import { ILuminaria } from 'modelos/src';
import {
  CodigoEstadoLuminaria,
  determinarCodigoEstado,
  presentacionEstado,
} from '../../estado/estado-luminaria';

const CODIGOS: CodigoEstadoLuminaria[] = [0, 1, 2, 3, 4, 5];
// Código cuyo drilldown muestra el detalle de alarmas.
const CODIGO_ALARMA: CodigoEstadoLuminaria = 3;
const COLOR_ALARMA_DEFAULT = 'gray';

@Component({
  selector: 'app-pie-chart-luminarias',
  templateUrl: './pie-chart-luminarias.component.html',
  styleUrl: './pie-chart-luminarias.component.scss',
  imports: [HighchartsChartModule],
})
export class PieChartLuminariasComponent implements OnChanges {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartInstance?: Highcharts.Chart;

  @Input() title = '';
  @Input() subtitle = '';
  @Input() data: ILuminaria[] = [];
  @Input() chartStyle = 'width: 100%; height: 200px; display: block';

  public seriesData: {
    name: string;
    y: number;
    drilldown?: string;
    color?: string;
  }[] = [];

  public drilldownData: {
    id: string;
    data: {
      name: string;
      y: number;
      color?: string;
    }[];
  }[] = [];

  saveInstance(chart: Highcharts.Chart) {
    this.chartInstance = chart;
  }

  // Alarmas activas de una luminaria (reporte periódico + energía).
  private alarmasDe(luminaria: ILuminaria): string[] {
    const periodicas =
      (luminaria.ultimoReportePeriodico?.valores as { alarmas?: string[] })
        ?.alarmas ?? [];
    const energia =
      (luminaria.ultimoReporteEnergia?.valores as { alarmas?: string[] })
        ?.alarmas ?? [];
    return [...periodicas, ...energia];
  }

  ngOnChanges(): void {
    if (this.chartInstance) {
      this.chartInstance.drillUp();
    }

    const total = this.data.length;

    // Código por luminaria (una sola vez).
    const codigos = this.data.map((lum) => determinarCodigoEstado(lum));

    // Serie principal: una porción por código presente.
    this.seriesData = CODIGOS.map((codigo) => {
      const cantidad = codigos.filter((c) => c === codigo).length;
      const presentacion = presentacionEstado(codigo);
      return {
        name: presentacion.label,
        y: total > 0 ? +((cantidad / total) * 100).toFixed(1) : 0,
        drilldown:
          codigo === CODIGO_ALARMA && cantidad > 0
            ? presentacion.label
            : undefined,
        color: presentacion.color,
      };
    }).filter((s) => s.y > 0);

    // Drilldown de alarmas (código 3): agrupar por codigo.codigo del modelo.
    const conteoAlarmas: {
      [grupo: string]: { cantidad: number; color?: string };
    } = {};

    this.data.forEach((luminaria, i) => {
      if (codigos[i] !== CODIGO_ALARMA) return;

      const codigosModelo =
        luminaria.dispositivo?.modeloDispositivo?.codigos?.codigos ?? [];

      this.alarmasDe(luminaria).forEach((alarma) => {
        const match = codigosModelo.find((c) => c.codigo === alarma);
        const grupo = match?.descripcion || alarma;
        const color = match?.categoriaEvento?.color;
        if (!conteoAlarmas[grupo]) {
          conteoAlarmas[grupo] = { cantidad: 0, color };
        }
        conteoAlarmas[grupo].cantidad += 1;
      });
    });

    const totalAlarmasIndividuales = Object.values(conteoAlarmas).reduce(
      (acc, { cantidad }) => acc + cantidad,
      0,
    );

    this.drilldownData =
      totalAlarmasIndividuales > 0
        ? [
            {
              id: presentacionEstado(CODIGO_ALARMA).label,
              data: Object.entries(conteoAlarmas).map(
                ([grupo, { cantidad, color }]) => ({
                  name: grupo,
                  y: +((cantidad / totalAlarmasIndividuales) * 100).toFixed(1),
                  color: color ?? COLOR_ALARMA_DEFAULT,
                }),
              ),
            },
          ]
        : [];

    const chartOptions: Highcharts.Options = {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        style: {
          color: 'var(--fg-grafico)',
        },
      },
      // Total en el centro de la dona
      title: {
        text: total.toLocaleString('es-AR'),
        verticalAlign: 'middle',
        floating: true,
        // Compensa la línea de base del texto: así queda en el centro del hueco
        y: 14,
        style: {
          color: 'var(--text-color)',
          fontSize: '16px',
          fontWeight: '600',
        },
      },
      subtitle: { text: this.subtitle },
      accessibility: {
        announceNewData: { enabled: true },
        point: { valueSuffix: '%' },
      },
      plotOptions: {
        pie: {
          size: '100%',
          innerSize: '62%',
          // Sin reserva para porciones separadas: la dona usa todo el alto
          slicedOffset: 0,
          center: ['50%', '50%'],
          // El borde de las porciones toma el fondo del panel
          borderColor: 'var(--bg-color-solid)',
          dataLabels: {
            enabled: false,
            format: '{point.name}: {point.y:.1f}%',
          },
        },
      },
      tooltip: {
        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
        pointFormatter: function (this: any) {
          const pct = (this.y ?? 0).toLocaleString('es-AR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
          return `<span style="color:${this.color}">${this.name}</span>: <b>${pct} %</b> del total<br/>`;
        },
      },
      xAxis: {
        labels: {
          style: {
            color: 'var(--fg-grafico)',
          },
        },
      },
      yAxis: {
        labels: {
          style: {
            color: 'var(--fg-grafico)',
          },
        },
      },
      // Sin leyenda: los chips de Estados/Código del panel ya muestran
      // nombres, colores y cantidades. El detalle va en el tooltip.
      legend: { enabled: false },
      exporting: {
        enabled: false,
      },
      series: [
        {
          name: 'Datos',
          colorByPoint: true,
          type: 'pie',
          data: this.seriesData,
        } as Highcharts.SeriesPieOptions,
      ],
      drilldown: {
        series: this.drilldownData.map((d) => ({
          id: d.id,
          type: 'pie',
          name: `Detalle de ${d.id}`,
          data: d.data,
        })),
      },
    };
    this.chartOptions = chartOptions;
  }
}
